// The computed texts a Saju book prints — a 대운's label and caption, a year's caption and score change, the rows of
// the year grids, the 대운 timeline and the month tables — made from one person's book chart (readBookChart) and the
// book's frame words (template-saju-book.mjs, or the Studio's edited copy). One implementation: the book builder prints
// these texts (export-book-figures.mjs) and records where each one is on the page, and the Studio makes them again for
// another person (specs/011). Formats are the builder's own (Python): `+3`, `-11` and `+0` for a score change, `+4.5`
// and `-6` for a term's change.
import { SajuChartError } from './template-saju-facts.mjs';

const fail = detail => { throw new SajuChartError(detail); };

// The builder's frame placeholders (build-product-samples.py PLACEHOLDER).
const PLACEHOLDER = /\{([a-z_][a-z0-9_]*)\}/g;

/**
 * A frame text with its {names} filled, as the builder's T() does it: a name the text does not have, or one of its
 * names left out, is an error rather than a guess. `frame` is { key: { label, text } } (SAJU_BOOK_FRAME).
 */
export function frameText(frame, key, values = {}) {
  const entry = frame?.[key];
  if (!entry || typeof entry.text !== 'string') throw new Error(`틀 문구 ${key} 가 없습니다.`);
  const names = [...new Set(Array.from(entry.text.matchAll(PLACEHOLDER), match => match[1]))].sort();
  const given = Object.keys(values).sort();
  if (names.join() !== given.join()) throw new Error(`틀 문구 ${key} 는 {${names.join(', ')}} 를 채웁니다 — 받은 값: ${given.join(', ') || '없음'}`);
  return entry.text.replace(PLACEHOLDER, (_, name) => String(values[name]));
}

// How the natal summary names each pillar: 을사년·신사월·갑술일·병자시.
const PILLAR_NAMES = Object.freeze({ year: '년', month: '월', day: '일', hour: '시' });

// The month themes by the month stem's ten god: frame words wolun-theme.<name>.title / .body.
const MONTH_THEMES = Object.freeze({
  편관: 'pyeongwan', 정관: 'jeonggwan', 편인: 'pyeonin', 정인: 'jeongin', 비견: 'bigyeon',
  겁재: 'geopjae', 식신: 'siksin', 상관: 'sanggwan', 편재: 'pyeonjae', 정재: 'jeongjae',
});

function integer(value, label) {
  if (!Number.isSafeInteger(value)) fail(`${label} 가 정수가 아닙니다.`);
  return value;
}
function text(value, label) {
  if (typeof value !== 'string' || !value) fail(`${label} 문자열이 없습니다.`);
  return value;
}

/** A score change as the builder prints it (f'{d:+d}'): the sign always, an ASCII minus. */
/**
 * The frame words bookFigures fills in (an edit of one changes the figures a book prints): the Studio keeps the edited ones
 * a book was built with beside its figures, so they are made again with the same words (component-saju-narrative.mjs).
 */
export const FIGURE_FRAME_KEYS = Object.freeze(['natal', 'page.2', 'daeun-read.caption', 'delta.first', 'seun-year.changes',
  'century-decade.2', 'century-decade.peak-heading', 'wolun.month-heading', 'wolun.quarter-subtitle', 'comparison.delta-caption',
  ...Object.values(MONTH_THEMES).flatMap(theme => [`wolun-theme.${theme}.title`, `wolun-theme.${theme}.body`])]);

const signedInteger = value => `${value < 0 ? '-' : '+'}${Math.abs(integer(value, '점수 변화'))}`;

/** A term's change as the builder prints it (f'{v:+g}'): terms are whole or half points, printed without a trailing .0. */
function signedTerm(value) {
  if (typeof value !== 'number' || !Number.isFinite(value) || Number(Math.abs(value).toPrecision(6)) !== Math.abs(value)) fail(`항 변화 ${value} 는 책의 서식으로 찍을 수 없습니다.`);
  return `${value < 0 ? '-' : '+'}${Math.abs(value)}`;
}

/** The birth as entered, as the book prints it: 1965.05.20 23:45, or the date and 시간 모름. */
function birthText(birth) {
  const solar = text(birth?.solar, '양력 출생 시각');
  return `${solar.slice(0, 10).replaceAll('-', '.')} ${birth.unknownTime ? '시간 모름' : solar.slice(11)}`;
}

/** The line under a score: where it moved from, or that it is the first. */
function scoreChange(frame, previous, current) {
  if (previous === undefined) return frameText(frame, 'delta.first', { current: integer(current, '점수') });
  const change = integer(current, '점수') - integer(previous, '이전 점수');
  return `${previous} → ${current}   ${change > 0 ? '상승' : change < 0 ? '하락' : '유지'} ${signedInteger(change)}`;
}

/**
 * Every computed text of a book, by key, for the person whose book chart this is:
 *   natal, person/birth — the natal summary (frame word natal) on most pages' sources, and the birth as entered
 *   person/name, person/label, book/data-id — given `name` (the chart has none) the page label (frame word page.2),
 *     given `dataId` the id of the data the book is made from (its colophon)
 *   daeun/<n>/label · caption · delta · ages · timeline-label · timeline-note     (n = 1–10)
 *   seun/<year>/label · caption · delta · source-tail · phase-year · daeun · balance · decade-label   (the 100 years)
 *   decade/<first year>/peak-heading                                               (the 100 years ten at a time)
 *   month/<month>/label · ten-gods · period-balance · theme-title · theme · heading · note   (the reference year)
 *   wolun/<quarter>/subtitle                                                       (1–4)
 *   comparison/daeun/caption · delta, comparison/seun/caption · delta              (into the reference year)
 * `book` is { chart, years, months } (readBookChart); `year` is the year the book reads as now — the year of its months,
 * its current 대운 and the change into it. A key the chart cannot give (a person whose reference year falls before their
 * first 대운 has no change into it) is absent, not guessed.
 */
export function bookFigures(book, { frame, year, name, dataId }) {
  const daeun = book?.chart?.daeun, years = book?.years, months = book?.months;
  if (!Array.isArray(daeun) || daeun.length !== 10) fail('대운 10개가 없습니다.');
  if (!Array.isArray(years) || years.length !== 100) fail('100년 세운이 없습니다.');
  if (!Array.isArray(months) || months.length !== 12) fail('월운 12개가 없습니다.');
  integer(year, '기준 연도');
  if (text(months[0].start_iso, '첫 달 시작').slice(0, 4) !== String(year)) fail(`월운이 ${year}년의 것이 아닙니다.`);
  const figures = {};
  // The person: the natal summary — pillars, day stem, the ten gods most first (the chart's order among equals) — and the
  // birth; the name and the data id only when given.
  const natal = book.chart.natal;
  const tenGods = Object.entries(book.chart.tenGods ?? fail('십성 수가 없습니다.')).map(([god, count]) => [god, integer(count, `${god} 수`)]);
  tenGods.sort((a, b) => b[1] - a[1]);
  figures['person/birth'] = birthText(book.chart.birth);
  figures.natal = frameText(frame, 'natal', {
    pillars: natal.map(pillar => `${pillar.stem}${pillar.branch}${PILLAR_NAMES[pillar.label]}`).join('·'),
    day_stem: natal.find(pillar => pillar.label === 'day').stem,
    ten_gods: tenGods.map(([god, count]) => `${god} ${count}`).join('·'),
    birth: figures['person/birth'],
  });
  if (name !== undefined) {
    figures['person/name'] = text(name, '이름');
    figures['person/label'] = frameText(frame, 'page.2', { name });
  }
  if (dataId !== undefined) figures['book/data-id'] = text(dataId, '자료 식별값');
  daeun.forEach((row, i) => {
    const at = `daeun/${integer(row.index, '대운 번호')}`;
    figures[`${at}/label`] = `${row.index}대운 ${text(row.ganji, '대운 간지')}`;
    figures[`${at}/caption`] = frameText(frame, 'daeun-read.caption', { start_age: integer(row.start_age, '대운 시작 나이'), end_age: integer(row.end_age, '대운 끝 나이'), stem: row.ten_god_stem, branch: row.ten_god_branch });
    figures[`${at}/delta`] = scoreChange(frame, i ? daeun[i - 1].balance : undefined, row.balance);
    figures[`${at}/ages`] = `${row.start_age}~${row.end_age}세`;
    figures[`${at}/timeline-label`] = `${row.index}대운 · ${row.ganji}`;
    figures[`${at}/timeline-note`] = `원문 · ${text(row.ten_god_stem, '대운 천간 십성')} / ${text(row.ten_god_branch, '대운 지지 십성')} · 참고 지수 ${integer(row.balance, '대운 점수')}`;
  });
  years.forEach((row, i) => {
    const at = `seun/${integer(row.year, '연도')}`;
    const inDaeun = `${integer(row.daeunIndex, '대운 번호')}대운 ${text(row.daeunGanji, '대운 간지')}`;
    figures[`${at}/label`] = `${row.year} ${text(row.ganji, '세운 간지')}`;
    figures[`${at}/daeun`] = inDaeun + (row.daeunChanged ? ' · 대운 전환' : '');
    figures[`${at}/caption`] = `${integer(row.age, '나이')}세 · ${text(row.stemTenGod, '세운 천간 십성')} / ${text(row.branchTenGod, '세운 지지 십성')} · ${figures[`${at}/daeun`]}`;
    figures[`${at}/delta`] = scoreChange(frame, row.previousBalance, row.balance);
    const changes = row.parts.filter(part => part.delta).map(part => `${part.label} ${signedTerm(part.delta)}`).join(' / ');
    figures[`${at}/source-tail`] = text(row.calculation, '계산 문장') + (changes ? ` ${frameText(frame, 'seun-year.changes')} ${changes}.` : '');
    figures[`${at}/phase-year`] = `${row.year} ${row.ganji} · 균형 ${row.balance}`;
    figures[`${at}/balance`] = String(row.balance);
    // The century book shows the hundred years ten at a time; the first highest score of the ten is their peak.
    const decade = years.slice(i - i % 10, i - i % 10 + 10);
    const peak = decade.reduce((best, candidate) => candidate.balance > best.balance ? candidate : best);
    figures[`${at}/decade-label`] = inDaeun + (row.daeunChanged ? ' · 전환' : '') + (row === peak ? frameText(frame, 'century-decade.2') : '');
    if (i % 10 === 0) figures[`decade/${row.year}/peak-heading`] = frameText(frame, 'century-decade.peak-heading', { year: peak.year });
  });
  months.forEach(row => {
    const at = `month/${integer(row.month, '월')}`, balance = integer(row.scores?.balance, '월 점수');
    const theme = MONTH_THEMES[row.stem_tg] ?? fail(`월 천간 십성 ${row.stem_tg} 의 주제가 없습니다.`);
    figures[`${at}/label`] = `${row.month}월 ${text(row.ganji, '월 간지')}`;
    figures[`${at}/ten-gods`] = `${row.stem_tg}·${text(row.branch_tg, '월 지지 십성')}`;
    figures[`${at}/period-balance`] = `${text(row.period, '절기 구간')}  균형 ${balance}`;
    figures[`${at}/theme-title`] = frameText(frame, `wolun-theme.${theme}.title`);
    figures[`${at}/theme`] = frameText(frame, `wolun-theme.${theme}.body`);
    figures[`${at}/heading`] = frameText(frame, 'wolun.month-heading', { month: row.month, ganji: row.ganji, theme: figures[`${at}/theme-title`] });
    figures[`${at}/note`] = `원문 · ${row.period} · ${row.stem_tg} / ${row.branch_tg} · 균형 ${balance}`;
  });
  for (let quarter = 1; quarter <= 4; quarter++) {
    figures[`wolun/${quarter}/subtitle`] = frameText(frame, 'wolun.quarter-subtitle', { first: months[quarter * 3 - 3].ganji, last: months[quarter * 3 - 1].ganji });
  }
  // The comparison page: the change into the reference year, and into the 대운 that year falls in.
  const now = years.findIndex(row => row.year === year);
  if (now > 0) {
    const before = years[now - 1], current = years[now];
    figures['comparison/seun/caption'] = frameText(frame, 'comparison.delta-caption', { compared: `${before.year}년 ${before.ganji} → ${current.year}년 ${current.ganji}` });
    figures['comparison/seun/delta'] = scoreChange(frame, current.previousBalance, current.balance);
  }
  const period = now < 0 ? -1 : daeun.findIndex(row => years[now].age >= row.start_age && years[now].age <= row.end_age);
  if (period > 0) {
    const before = daeun[period - 1], current = daeun[period];
    figures['comparison/daeun/caption'] = frameText(frame, 'comparison.delta-caption', { compared: `${before.index}대운 ${before.ganji} → ${current.index}대운 ${current.ganji}` });
    figures['comparison/daeun/delta'] = scoreChange(frame, before.balance, current.balance);
  }
  return figures;
}
